import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, ShieldCheck, Upload, FileText, CheckCircle,
  XCircle, Clock, AlertTriangle, ChevronRight, Loader2, Crown, Camera,
} from "lucide-react";

const UploadBox = ({ label, file, uploading, onUpload, testId }) => {
  const fileRef = useRef(null);
  return (
    <div
      onClick={() => !uploading && fileRef.current?.click()}
      className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:border-amber-400 transition-colors cursor-pointer"
      data-testid={testId}
    >
      {file ? (
        <div className="flex items-center justify-center gap-2 text-emerald-600">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium text-sm truncate max-w-[180px]">{file.name}</span>
        </div>
      ) : uploading ? (
        <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
      ) : (
        <>
          <Camera className="w-8 h-8 text-slate-400 mx-auto mb-1" />
          <p className="text-slate-500 text-xs font-medium">{label}</p>
          <p className="text-slate-400 text-[10px] mt-0.5">Click to upload</p>
        </>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
      />
    </div>
  );
};

const KYCVerification = ({ user, onStatusChange }) => {
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  const [idType, setIdType] = useState("national_id");
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [addressProof, setAddressProof] = useState(null);
  const addressRef = useRef(null);

  useEffect(() => { fetchKYCStatus(); }, []);

  const fetchKYCStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/user/kyc-status`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
      });
      setKycStatus(res.data);
    } catch (error) {
      console.error("Failed to fetch KYC status:", error);
    } finally { setLoading(false); }
  };

  const handleUpload = async (file, key) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      const uploaded = { name: file.name, url: res.data.file_url };
      if (key === "id_front") setIdFront(uploaded);
      else if (key === "id_back") setIdBack(uploaded);
      else if (key === "address") setAddressProof(uploaded);
      toast.success("File uploaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally { setUploading((prev) => ({ ...prev, [key]: false })); }
  };

  const handleSubmit = async () => {
    if (!idFront) {
      toast.error("Please upload the front of your ID");
      return;
    }
    if (!idBack) {
      toast.error("Please upload the back of your ID");
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const docList = [
        { document_type: `${idType}_front`, file_url: idFront.url },
        { document_type: `${idType}_back`, file_url: idBack.url },
      ];
      if (addressProof) {
        docList.push({ document_type: "proof_of_address", file_url: addressProof.url });
      }
      await axios.post(`${API}/user/kyc-submit`, { documents: docList }, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
      });
      toast.success("Documents submitted for review!");
      setDialogOpen(false);
      setIdFront(null);
      setIdBack(null);
      setAddressProof(null);
      fetchKYCStatus();
      if (onStatusChange) onStatusChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  const getTierInfo = (tier) => {
    const tiers = {
      1: { name: "Basic", icon: <Shield className="w-5 h-5" />, color: "text-slate-600", bg: "bg-slate-100", access: ["Silver Package"] },
      2: { name: "Verified", icon: <ShieldCheck className="w-5 h-5" />, color: "text-emerald-600", bg: "bg-emerald-100", access: ["All Packages", "Higher Limits"] },
      3: { name: "Premium", icon: <Crown className="w-5 h-5" />, color: "text-amber-600", bg: "bg-amber-100", access: ["All Packages", "Priority Support"] },
    };
    return tiers[tier] || tiers[1];
  };

  const getStatusBadge = (status) => {
    const badges = {
      none: { color: "bg-slate-100 text-slate-600", icon: <Shield className="w-4 h-4" />, text: "Not Verified" },
      pending: { color: "bg-amber-100 text-amber-700", icon: <Clock className="w-4 h-4" />, text: "Pending Review" },
      under_review: { color: "bg-blue-100 text-blue-700", icon: <Clock className="w-4 h-4" />, text: "Under Review" },
      approved: { color: "bg-emerald-100 text-emerald-700", icon: <CheckCircle className="w-4 h-4" />, text: "Verified" },
      rejected: { color: "bg-rose-100 text-rose-700", icon: <XCircle className="w-4 h-4" />, text: "Rejected" },
    };
    return badges[status] || badges.none;
  };

  if (loading) {
    return (
      <Card className="bg-white shadow-lg border-slate-200">
        <CardContent className="p-6 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-600" />
        </CardContent>
      </Card>
    );
  }

  const currentTier = kycStatus?.tier || 1;
  const tierInfo = getTierInfo(currentTier);
  const statusBadge = getStatusBadge(kycStatus?.kyc_status || "none");

  return (
    <Card className="bg-white shadow-lg border-slate-200" data-testid="kyc-verification-card">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-slate-800 font-heading flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" /> Account Verification
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {/* Current Tier */}
        <div className="flex items-center justify-between mb-6 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 ${tierInfo.bg} rounded-xl flex items-center justify-center ${tierInfo.color}`}>
              {tierInfo.icon}
            </div>
            <div>
              <p className="text-sm text-slate-500">Current Tier</p>
              <p className="text-lg font-semibold text-slate-800">Tier {currentTier}: {tierInfo.name}</p>
            </div>
          </div>
          <div className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${statusBadge.color}`}>
            {statusBadge.icon} {statusBadge.text}
          </div>
        </div>

        {/* Access Info */}
        <div className="mb-6">
          <p className="text-sm text-slate-500 mb-2">Package Access:</p>
          <div className="flex flex-wrap gap-2">
            {tierInfo.access.map((item, i) => (
              <span key={i} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm">{item}</span>
            ))}
          </div>
        </div>

        {/* Upgrade Prompt */}
        {currentTier < 2 && kycStatus?.kyc_status !== "pending" && kycStatus?.kyc_status !== "under_review" && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-800 font-medium">Upgrade to Verified Tier</p>
                <p className="text-amber-700 text-sm mt-1">Submit your ID (front & back) to access Gold and Platinum packages.</p>
              </div>
            </div>
          </div>
        )}

        {/* Rejected */}
        {kycStatus?.kyc_status === "rejected" && kycStatus?.kyc_details?.rejection_reason && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-rose-800 font-medium">Verification Rejected</p>
                <p className="text-rose-700 text-sm mt-1">Reason: {kycStatus.kyc_details.rejection_reason}</p>
                <p className="text-rose-600 text-xs mt-2">Please submit new documents to try again.</p>
              </div>
            </div>
          </div>
        )}

        {/* Pending */}
        {(kycStatus?.kyc_status === "pending" || kycStatus?.kyc_status === "under_review") && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5 animate-pulse" />
              <div>
                <p className="text-blue-800 font-medium">Verification In Progress</p>
                <p className="text-blue-700 text-sm mt-1">Your documents are being reviewed. This typically takes 1-2 business days.</p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        {currentTier < 2 && kycStatus?.kyc_status !== "pending" && kycStatus?.kyc_status !== "under_review" && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="w-full btn-gold gap-2" data-testid="start-verification-btn">
                <Upload className="w-5 h-5" />
                {kycStatus?.kyc_status === "rejected" ? "Resubmit Verification" : "Start Verification"}
                <ChevronRight className="w-5 h-5" />
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-slate-800 font-heading">Identity Verification</DialogTitle>
              </DialogHeader>
              <div className="space-y-5 pt-4 max-h-[75vh] overflow-y-auto">
                <p className="text-slate-600 text-sm">Upload front and back of your government-issued ID to verify your identity.</p>

                {/* ID Type */}
                <div className="space-y-2">
                  <Label className="text-slate-700">ID Document Type</Label>
                  <select value={idType} onChange={(e) => setIdType(e.target.value)}
                    className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm" data-testid="id-type-select">
                    <option value="national_id">National ID Card</option>
                    <option value="drivers_license">Driver's License</option>
                    <option value="passport">Passport</option>
                  </select>
                </div>

                {/* Front & Back Upload */}
                <div className="space-y-2">
                  <Label className="text-slate-700">Upload {idType === "passport" ? "Photo Page" : "Front & Back"} *</Label>
                  <div className={`grid ${idType === "passport" ? "grid-cols-1" : "grid-cols-2"} gap-3`}>
                    <UploadBox
                      label={idType === "passport" ? "Photo Page" : "Front Side"}
                      file={idFront}
                      uploading={uploading.id_front}
                      onUpload={(f) => handleUpload(f, "id_front")}
                      testId="upload-id-front"
                    />
                    {idType !== "passport" && (
                      <UploadBox
                        label="Back Side"
                        file={idBack}
                        uploading={uploading.id_back}
                        onUpload={(f) => handleUpload(f, "id_back")}
                        testId="upload-id-back"
                      />
                    )}
                  </div>
                  <p className="text-slate-400 text-xs">PNG, JPG or PDF (max 10MB each)</p>
                </div>

                {/* Proof of Address */}
                <div className="space-y-2">
                  <Label className="text-slate-700">Proof of Address (Optional)</Label>
                  <div
                    onClick={() => !uploading.address && addressRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:border-amber-400 transition-colors cursor-pointer"
                    data-testid="upload-address-proof"
                  >
                    {addressProof ? (
                      <div className="flex items-center justify-center gap-2 text-emerald-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium text-sm truncate max-w-[200px]">{addressProof.name}</span>
                      </div>
                    ) : uploading.address ? (
                      <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
                    ) : (
                      <>
                        <FileText className="w-8 h-8 text-slate-400 mx-auto mb-1" />
                        <p className="text-slate-500 text-xs font-medium">Click to upload address proof</p>
                        <p className="text-slate-400 text-[10px] mt-0.5">Utility bill or bank statement (last 3 months)</p>
                      </>
                    )}
                    <input
                      ref={addressRef}
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0], "address")}
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 font-medium mb-2">Upload Checklist:</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      {idFront ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <div className="w-4 h-4 rounded-full border-2 border-slate-300" />}
                      <span className={idFront ? "text-emerald-700" : "text-slate-500"}>{idType === "passport" ? "Photo Page" : "ID Front"}</span>
                    </div>
                    {idType !== "passport" && (
                      <div className="flex items-center gap-2 text-sm">
                        {idBack ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <div className="w-4 h-4 rounded-full border-2 border-slate-300" />}
                        <span className={idBack ? "text-emerald-700" : "text-slate-500"}>ID Back</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      {addressProof ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <div className="w-4 h-4 rounded-full border-2 border-slate-300" />}
                      <span className={addressProof ? "text-emerald-700" : "text-slate-500"}>Address Proof (optional)</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !idFront || (idType !== "passport" && !idBack)}
                  className="w-full btn-gold"
                  data-testid="submit-kyc-btn"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Upload className="w-5 h-5 mr-2" />}
                  Submit for Verification
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Already Verified */}
        {currentTier >= 2 && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="text-emerald-800 font-medium">Identity Verified</p>
                <p className="text-emerald-700 text-sm">You have full access to all investment packages.</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KYCVerification;
